import React, { useState } from 'react';
import { 
  CurrencyDollarIcon, 
  UsersIcon, 
  CalendarIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

interface RevenueDistribution {
  id: string;
  workTitle: string;
  workId: string;
  totalAmount: number;
  currency: string;
  distributionDate: number;
  status: 'completed' | 'pending' | 'processing';
  participants: {
    name: string;
    address: string;
    sharePercentage: number;
    receivedAmount: number;
    status: 'completed' | 'pending';
  }[];
  transactionHash?: string;
}

interface RevenueDistributionHistoryProps {
  distributions: RevenueDistribution[];
}

const RevenueDistributionHistory: React.FC<RevenueDistributionHistoryProps> = ({ distributions }) => {
  const [selectedDistribution, setSelectedDistribution] = useState<RevenueDistribution | null>(null);
  const [viewMode, setViewMode] = useState<'timeline' | 'table'>('timeline');

  const formatCurrency = (amount: number, currency: string = 'SOL') => {
    return `${amount.toFixed(2)} ${currency}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'processing': return 'text-yellow-600 bg-yellow-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="w-4 h-4" />;
      case 'processing': return <ClockIcon className="w-4 h-4" />;
      case 'pending': return <ClockIcon className="w-4 h-4" />;
      default: return <ClockIcon className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return '已完成';
      case 'processing': return '处理中';
      case 'pending': return '待处理';
      default: return '未知';
    }
  };

  return (
    <div className="space-y-6">
      {/* 标题和控制 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">收益分配历史</h2>
          <p className="text-gray-600 mt-1">查看所有作品的收益分配记录和状态</p>
        </div>
        
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('timeline')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              viewMode === 'timeline'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <CalendarIcon className="w-4 h-4 inline mr-1" />
            时间线
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              viewMode === 'table'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ChartBarIcon className="w-4 h-4 inline mr-1" />
            表格
          </button>
        </div>
      </div>

      {/* 统计概览 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <CurrencyDollarIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-600 font-medium">总分配金额</p>
              <p className="text-xl font-bold text-blue-700">
                {formatCurrency(distributions.reduce((sum, d) => sum + d.totalAmount, 0))}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-600 font-medium">已完成分配</p>
              <p className="text-xl font-bold text-green-700">
                {distributions.filter(d => d.status === 'completed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <UsersIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-purple-600 font-medium">参与者总数</p>
              <p className="text-xl font-bold text-purple-700">
                {distributions.reduce((sum, d) => sum + d.participants.length, 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <CalendarIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm text-orange-600 font-medium">本月分配</p>
              <p className="text-xl font-bold text-orange-700">
                {distributions.filter(d => 
                  new Date(d.distributionDate).getMonth() === new Date().getMonth()
                ).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 时间线视图 */}
      {viewMode === 'timeline' && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6">
            <div className="relative">
              {/* 时间线 */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              
              <div className="space-y-8">
                {distributions.map((distribution, index) => (
                  <div key={distribution.id} className="relative flex items-start gap-6">
                    {/* 时间线节点 */}
                    <div className={`relative z-10 w-16 h-16 rounded-full flex items-center justify-center ${
                      distribution.status === 'completed' 
                        ? 'bg-green-500' 
                        : distribution.status === 'processing'
                        ? 'bg-yellow-500'
                        : 'bg-gray-400'
                    }`}>
                      {getStatusIcon(distribution.status)}
                      <div className="text-white">
                        {distribution.status === 'completed' && <CheckCircleIcon className="w-6 h-6" />}
                        {distribution.status === 'processing' && <ClockIcon className="w-6 h-6" />}
                        {distribution.status === 'pending' && <ClockIcon className="w-6 h-6" />}
                      </div>
                    </div>

                    {/* 分配详情卡片 */}
                    <div className="flex-1 bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors cursor-pointer"
                         onClick={() => setSelectedDistribution(distribution)}>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{distribution.workTitle}</h3>
                          <p className="text-sm text-gray-500">{formatDate(distribution.distributionDate)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">
                            {formatCurrency(distribution.totalAmount, distribution.currency)}
                          </p>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(distribution.status)}`}>
                            {getStatusIcon(distribution.status)}
                            {getStatusText(distribution.status)}
                          </span>
                        </div>
                      </div>

                      {/* 参与者预览 */}
                      <div className="flex items-center gap-4">
                        <div className="flex -space-x-2">
                          {distribution.participants.slice(0, 4).map((participant, idx) => (
                            <div key={idx} className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium border-2 border-white">
                              {participant.name.charAt(0)}
                            </div>
                          ))}
                          {distribution.participants.length > 4 && (
                            <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-xs font-medium border-2 border-white">
                              +{distribution.participants.length - 4}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          {distribution.participants.length} 位参与者
                        </p>
                        <ArrowRightIcon className="w-4 h-4 text-gray-400 ml-auto" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 表格视图 */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    作品
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    分配金额
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    参与者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {distributions.map((distribution) => (
                  <tr key={distribution.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{distribution.workTitle}</div>
                        <div className="text-sm text-gray-500">ID: {distribution.workId}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-blue-600">
                        {formatCurrency(distribution.totalAmount, distribution.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-1">
                          {distribution.participants.slice(0, 3).map((participant, idx) => (
                            <div key={idx} className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-medium border border-white">
                              {participant.name.charAt(0)}
                            </div>
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">
                          {distribution.participants.length}人
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(distribution.status)}`}>
                        {getStatusIcon(distribution.status)}
                        {getStatusText(distribution.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(distribution.distributionDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedDistribution(distribution)}
                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                      >
                        <EyeIcon className="w-4 h-4" />
                        查看详情
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 详情模态框 */}
      {selectedDistribution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedDistribution.workTitle}</h3>
                  <p className="text-sm text-gray-500">分配详情</p>
                </div>
                <button
                  onClick={() => setSelectedDistribution(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* 分配概览 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">总分配金额</p>
                      <p className="text-lg font-semibold text-blue-600">
                        {formatCurrency(selectedDistribution.totalAmount, selectedDistribution.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">分配时间</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatDate(selectedDistribution.distributionDate)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 参与者详情 */}
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-4">参与者分配详情</h4>
                  <div className="space-y-3">
                    {selectedDistribution.participants.map((participant, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                            {participant.name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{participant.name}</p>
                            <p className="text-sm text-gray-500">{participant.address.substring(0, 10)}...</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">
                            {formatCurrency(participant.receivedAmount, selectedDistribution.currency)}
                          </p>
                          <p className="text-sm text-gray-500">{participant.sharePercentage}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 交易哈希 */}
                {selectedDistribution.transactionHash && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">交易哈希</p>
                    <p className="text-sm font-mono bg-gray-100 p-2 rounded break-all">
                      {selectedDistribution.transactionHash}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueDistributionHistory;
